// The Fleet page's Machines list as pure data (#6560, epic #6552): the filter chips and
// their counts, search, the sort (and the List view's header press, #6561), group by server
// with its default rule and header summary, the Cards view's 12-card paging (#6785), and the URL state behind
// all of it (owner ruling G10). The List view's page lives in `fleet-pager.ts`.
// `FleetMachines` (the presenter) only renders what this returns.
//
// EVERY JUDGEMENT IS THE SERVER'S, as in `fleet-attention.ts`: a machine's status, health
// and attention flag are its `verdict` (#6557), and its server is `server.key` (owner
// ruling G7: the label, else the shared address, else the machine itself). This file
// counts, matches, orders and groups those; it never re-derives one from a raw reading.
// One exception to grouping by `server.key`: every `machine:<id>` key (no label, no
// address: the API could not place it) folds into one trailing "Server not known" group,
// which does not count toward the 3-server default. The prefix is the API's `serverKey`
// (`apps/api/src/routes/fleet-utilization-box.ts`).
//
// CLIENT-SIDE OVER THE WHOLE FLEET. `GET /v1/fleet/utilization` answers every machine in
// one read, so filter, search, sort and paging run here. The trip condition for paging on
// the server is an account above 500 machines (#6560 § Decisions).
//
// THE URL IS PARSED, NEVER TRUSTED. Each of `filter`, `q`, `sort`, `dir`, `group` and
// `view` falls back to its default on its own when it does not parse, so a hand-edited or
// stale link shows the default view for that key and keeps the rest.

import type { Translator } from '@developerz/i18n';
import { z } from 'zod';
import type { UtilBox } from './fleetUtilization';
import { shownJob } from './shown-job';

/** The chips, in the prototype's order. */
export const MACHINE_FILTERS = ['all', 'working', 'idle', 'attention', 'offline'] as const;
export type MachineFilter = (typeof MACHINE_FILTERS)[number];

/**
 * `default` is "Needs attention first, then highest CPU"; the rest name one column. `task`
 * (Doing now) and `seen` (Last seen) are the List view's (#6561), which sorts every column.
 */
export const MACHINE_SORTS = [
  'default',
  'name',
  'status',
  'task',
  'cpu',
  'memory',
  'disk',
  'health',
  'seen',
] as const;
export type MachineSort = (typeof MACHINE_SORTS)[number];

export const SORT_DIRS = ['asc', 'desc'] as const;
export type SortDir = (typeof SORT_DIRS)[number];

/** Cards (#6560), List (#6561) and Map (#6562). */
export const MACHINE_VIEWS = ['cards', 'list', 'map'] as const;
export type MachineView = (typeof MACHINE_VIEWS)[number];

/** An explicit choice; absent (`null`) means the default rule ({@link groupedByDefault}). */
export const GROUP_CHOICES = ['on', 'off'] as const;
export type GroupChoice = (typeof GROUP_CHOICES)[number];

/** A search longer than this is not a search a person typed. */
export const SEARCH_MAX = 200;

/** Search shows only above this many machines (the prototype's `all.length > 10`). */
export const SEARCH_ABOVE = 10;

/** Cards shown first, and how many each "Show 12 more" adds (#6785). */
export const CARDS_PAGE = 12;

/**
 * The most cards the Cards view shows (#6785, prototype `:4788-4795`): a Mission Panel card
 * is tall, so past sixty the List or the Map shows the whole fleet.
 */
export const CARDS_MAX = 60;

/** Group by server turns itself on when the fleet spans at least this many servers. */
export const GROUP_AT_SERVERS = 3;

export type FleetViewState = {
  readonly filter: MachineFilter;
  readonly q: string;
  readonly sort: MachineSort;
  readonly dir: SortDir;
  readonly group: GroupChoice | null;
  readonly view: MachineView;
};

export const DEFAULT_FLEET_VIEW: FleetViewState = {
  filter: 'all',
  q: '',
  sort: 'default',
  dir: 'desc',
  group: null,
  view: 'cards',
};

// Each field catches its own failure, an absent key included, so the object parse never
// throws and one bad value never takes the others with it. A repeated key arrives as an
// array, which no field accepts.
const FleetViewSchema = z.object({
  filter: z.enum(MACHINE_FILTERS).catch(DEFAULT_FLEET_VIEW.filter),
  q: z.string().max(SEARCH_MAX).catch(DEFAULT_FLEET_VIEW.q),
  sort: z.enum(MACHINE_SORTS).catch(DEFAULT_FLEET_VIEW.sort),
  dir: z.enum(SORT_DIRS).catch(DEFAULT_FLEET_VIEW.dir),
  group: z.enum(GROUP_CHOICES).nullable().catch(DEFAULT_FLEET_VIEW.group),
  view: z.enum(MACHINE_VIEWS).catch(DEFAULT_FLEET_VIEW.view),
});

/** The list's view state off `useSearchParams`' value; malformed keys read as the default. */
export function parseFleetView(params: Partial<Record<string, string | string[]>>): FleetViewState {
  return FleetViewSchema.parse(params);
}

/** The search params to write for `state`: a default value clears its key. */
export function fleetViewParams(
  state: FleetViewState,
): Record<keyof FleetViewState, string | undefined> {
  const differs = <K extends keyof FleetViewState>(key: K): string | undefined => {
    const value = state[key];
    return value === DEFAULT_FLEET_VIEW[key] || value === null ? undefined : String(value);
  };
  return {
    filter: differs('filter'),
    q: state.q.trim() === '' ? undefined : state.q,
    sort: differs('sort'),
    dir: differs('dir'),
    group: differs('group'),
    view: differs('view'),
  };
}

/** What choosing a filter writes, chip or tile alike: "All" also clears the search (the prototype's `filter`). */
export const filterPatch = (
  filter: MachineFilter,
): Pick<FleetViewState, 'filter'> & { q?: string } =>
  filter === 'all' ? { filter, q: '' } : { filter };

/** The 8 characters a person reads beside a machine's name (#6443). */
export const shortId = (id: string): string => id.slice(0, 8);

/**
 * The statuses of a machine that is not checking in: the Offline chip, the Map's off
 * square and the panel's paused-history rule (#6565) read this one list.
 */
export const OFFLINE_STATUSES: readonly UtilBox['verdict']['status'][] = [
  'offline',
  'not_responding',
];

/** Whether one machine is under one chip. */
export function matchesFilter(b: UtilBox, filter: MachineFilter): boolean {
  switch (filter) {
    case 'all':
      return true;
    case 'working':
    case 'idle':
      return b.verdict.status === filter;
    case 'attention':
      return b.verdict.needs_attention;
    case 'offline':
      return OFFLINE_STATUSES.includes(b.verdict.status);
  }
}

/** Every chip's count, off the whole fleet (the chips never count the search). */
export function filterCounts(boxes: readonly UtilBox[]): Record<MachineFilter, number> {
  const counts = { all: 0, working: 0, idle: 0, attention: 0, offline: 0 };
  for (const b of boxes) for (const f of MACHINE_FILTERS) if (matchesFilter(b, f)) counts[f] += 1;
  return counts;
}

/** The machines whose name, id or server (label or address) contains `q`, any case. */
export function searchMachines(boxes: readonly UtilBox[], q: string): UtilBox[] {
  const needle = q.trim().toLowerCase();
  if (needle === '') return [...boxes];
  return boxes.filter((b) =>
    [b.display_name, b.id, b.server.label ?? '', b.server.address ?? ''].some((s) =>
      s.toLowerCase().includes(needle),
    ),
  );
}

/** Worst first, for the Health column: the verdict's own fold order. */
const HEALTH_RANK: Readonly<Record<UtilBox['verdict']['health'], number>> = {
  offline: 9,
  not_responding: 9,
  overloaded: 8,
  disk_critical: 7,
  memory_critical: 7,
  disk_warning: 6,
  memory_warning: 6,
  busy: 5,
  healthy: 4,
  unknown: 0,
};

/** Busiest first, for the Status column. */
const STATUS_RANK: Readonly<Record<UtilBox['verdict']['status'], number>> = {
  working: 7,
  idle: 6,
  starting: 5,
  paused: 4,
  waiting_to_connect: 3,
  not_responding: 2,
  offline: 1,
  unknown: 0,
};

const byName = (a: UtilBox, b: UtilBox): number =>
  a.display_name.localeCompare(b.display_name, undefined, { numeric: true }) ||
  a.id.localeCompare(b.id);

/**
 * What a machine is doing, as Doing now sorts it: the title of the job the cell names
 * (`shownJob`: its oldest RUNNING job of THIS account's, else its oldest, #6745), else nothing. A machine with none (idle, paused, offline,
 * another account's work) sorts last both ways, ties by name, as the prototype's
 * `task || '~'` does; the cell's status sentence ("Waiting for work") is not a task and is
 * not sorted as one.
 */
const taskTitle = (b: UtilBox): string | null =>
  b.verdict.status === 'working' ? (shownJob(b)?.job.title ?? null) : null;

/**
 * A column's number, or null when the machine reported none (sorted last either way).
 * `seen` is how long ago the last live check-in was, so "descending" reads the
 * longest-silent machine first, as the prototype's seconds-ago key does. The API sends no
 * `last_beat_iso` once a machine's presence has expired, so an offline or not-responding
 * machine has no readable check-in and sorts last in both directions.
 */
function columnValue(
  b: UtilBox,
  sort: Exclude<MachineSort, 'default' | 'name' | 'task'>,
): number | null {
  switch (sort) {
    case 'seen': {
      const at = b.last_beat_iso === null ? Number.NaN : Date.parse(b.last_beat_iso);
      return Number.isNaN(at) ? null : -at;
    }
    case 'status':
      return STATUS_RANK[b.verdict.status];
    case 'health':
      return HEALTH_RANK[b.verdict.health];
    case 'cpu':
    case 'memory':
    case 'disk':
      return b.verdict.signals[sort].ratio;
  }
}

/**
 * The list's order. `default`: needs attention first, then the highest CPU (load over
 * processors), then the name; it ignores `dir`. A column sort follows `dir`, a missing
 * reading last both ways, ties by name.
 */
export function sortMachines(
  boxes: readonly UtilBox[],
  sort: MachineSort,
  dir: SortDir,
): UtilBox[] {
  const sign = dir === 'asc' ? 1 : -1;
  const compare = (a: UtilBox, b: UtilBox): number => {
    if (sort === 'default') {
      const attention = Number(b.verdict.needs_attention) - Number(a.verdict.needs_attention);
      const cpu = (b.verdict.signals.cpu.ratio ?? -1) - (a.verdict.signals.cpu.ratio ?? -1);
      return attention || cpu || byName(a, b);
    }
    if (sort === 'name') return sign * byName(a, b);
    if (sort === 'task')
      return missingLast(taskTitle(a), taskTitle(b), sign, byText) || byName(a, b);
    return missingLast(columnValue(a, sort), columnValue(b, sort), sign, byNumber) || byName(a, b);
  };
  return [...boxes].sort(compare);
}

const byText = (a: string, b: string): number => a.localeCompare(b, undefined, { numeric: true });
const byNumber = (a: number, b: number): number => a - b;

/** `dir`-signed order of two values, a missing one (`null`) last in either direction. */
function missingLast<V>(
  va: V | null,
  vb: V | null,
  sign: number,
  order: (a: V, b: V) => number,
): number {
  if (va === null || vb === null) return (va === null ? 1 : 0) - (vb === null ? 1 : 0);
  return sign * order(va, vb);
}

/** The List view's sortable columns, in the table's order (#6561): every column but the row menu. */
export const LIST_COLUMNS = [
  'name',
  'status',
  'task',
  'cpu',
  'memory',
  'disk',
  'health',
  'seen',
] as const satisfies readonly Exclude<MachineSort, 'default'>[];
export type ListColumn = (typeof LIST_COLUMNS)[number];

/** The columns a person reads as text: a first press sorts them A to Z; numbers start high. */
const TEXT_COLUMNS: readonly ListColumn[] = ['name', 'status', 'task'];

/** What a press on a column's header writes: a new column starts at its own end, the same one flips. */
export function sortPress(
  view: Pick<FleetViewState, 'sort' | 'dir'>,
  column: ListColumn,
): Pick<FleetViewState, 'sort' | 'dir'> {
  if (view.sort === column) return { sort: column, dir: view.dir === 'asc' ? 'desc' : 'asc' };
  return { sort: column, dir: TEXT_COLUMNS.includes(column) ? 'asc' : 'desc' };
}

/**
 * A column header's `aria-sort`. Under the default order no column is marked: it ranks
 * needs attention before CPU, so no single column is what it is sorted by.
 */
export function ariaSort(
  view: Pick<FleetViewState, 'sort' | 'dir'>,
  column: ListColumn,
): 'ascending' | 'descending' | 'none' {
  if (view.sort !== column) return 'none';
  return view.dir === 'asc' ? 'ascending' : 'descending';
}

/** The group the API could not place on any server: one machine per `machine:` key. */
export const UNPLACED_GROUP = 'machine:';

const placed = (key: string): boolean => !key.startsWith(UNPLACED_GROUP);

/** Distinct servers the fleet spans; machines on no known server are not counted. */
export function groupedByDefault(boxes: readonly UtilBox[]): boolean {
  return new Set(boxes.map((b) => b.server.key).filter(placed)).size >= GROUP_AT_SERVERS;
}

/** What a group header names: the label, else "Unnamed server, <address>", else unknown. */
export type ServerName =
  | { readonly kind: 'label'; readonly label: string }
  | { readonly kind: 'address'; readonly address: string }
  | { readonly kind: 'unknown' };

/** The header's verdict on a whole server, worst first (the prototype's `summary`). */
export type ServerSummary =
  | { readonly kind: 'overloaded'; readonly count: number }
  | { readonly kind: 'attention'; readonly count: number }
  | { readonly kind: 'busy'; readonly count: number }
  | { readonly kind: 'healthy' };

export type ServerGroup = {
  readonly key: string;
  readonly name: ServerName;
  /** The machines of this server that passed the filter and search, in list order. */
  readonly rows: readonly UtilBox[];
  /** Every machine on the server, whatever the filter: the header's count. */
  readonly count: number;
  readonly summary: ServerSummary;
};

function serverName(b: UtilBox): ServerName {
  if (!placed(b.server.key)) return { kind: 'unknown' };
  if (b.server.label !== null) return { kind: 'label', label: b.server.label };
  return b.server.address !== null
    ? { kind: 'address', address: b.server.address }
    : { kind: 'unknown' };
}

function serverSummary(machines: readonly UtilBox[]): ServerSummary {
  const count = (pred: (b: UtilBox) => boolean): number => machines.filter(pred).length;
  const overloaded = count((b) => b.verdict.health === 'overloaded');
  if (overloaded > 0) return { kind: 'overloaded', count: overloaded };
  const attention = count((b) => b.verdict.needs_attention);
  if (attention > 0) return { kind: 'attention', count: attention };
  const busy = count((b) => b.verdict.health === 'busy');
  return busy > 0 ? { kind: 'busy', count: busy } : { kind: 'healthy' };
}

/** The group a machine falls in: its server key, or the one shared unplaced group. */
const groupKey = (b: UtilBox): string => (placed(b.server.key) ? b.server.key : UNPLACED_GROUP);

/**
 * `rows` (already filtered, searched and sorted) split by server. A group is ordered by
 * where its first machine falls in `rows`, so the server holding the top machine leads;
 * the unplaced machines share one group, always last. Count and summary read the whole
 * server off `all`.
 */
export function serverGroups(all: readonly UtilBox[], rows: readonly UtilBox[]): ServerGroup[] {
  const byKey = new Map<string, UtilBox[]>();
  for (const b of rows) {
    const key = groupKey(b);
    byKey.set(key, [...(byKey.get(key) ?? []), b]);
  }
  const keys = [...byKey.keys()].sort(
    (a, b) => Number(a === UNPLACED_GROUP) - Number(b === UNPLACED_GROUP),
  );
  return keys.map((key) => {
    const members = byKey.get(key) ?? [];
    const onServer = all.filter((b) => groupKey(b) === key);
    const first = members[0];
    return {
      key,
      name: first === undefined || key === UNPLACED_GROUP ? { kind: 'unknown' } : serverName(first),
      rows: members,
      count: onServer.length,
      summary: serverSummary(onServer),
    };
  });
}

export type MachineList = {
  /** Filtered, searched and sorted. */
  readonly rows: readonly UtilBox[];
  readonly total: number;
  /** The groups when grouped by server, else null. */
  readonly groups: readonly ServerGroup[] | null;
};

/** The whole list for one view state. */
export function machineList(all: readonly UtilBox[], state: FleetViewState): MachineList {
  const rows = sortMachines(
    searchMachines(
      all.filter((b) => matchesFilter(b, state.filter)),
      state.q,
    ),
    state.sort,
    state.dir,
  );
  const grouped = state.group === null ? groupedByDefault(all) : state.group === 'on';
  return { rows, total: rows.length, groups: grouped ? serverGroups(all, rows) : null };
}

/** One rendered stretch of the list: a group's header (or none) and its shown machines. */
export type PagedGroup = { readonly group: ServerGroup | null; readonly rows: readonly UtilBox[] };

/**
 * `limit` machines from the `from`-th (0-based, in list order), a group cut part-way keeping
 * its header. The Cards view reads from 0 ("Show 60 more"); the List view's page reads from
 * its first row (`fleet-pager.ts`).
 */
export function pageGroups(list: MachineList, limit: number, from = 0): PagedGroup[] {
  const stretches: PagedGroup[] =
    list.groups === null
      ? [{ group: null, rows: list.rows }]
      : list.groups.map((g) => ({ group: g, rows: g.rows }));
  const out: PagedGroup[] = [];
  let skip = from;
  let left = limit;
  for (const s of stretches) {
    if (left <= 0) break;
    if (skip > 0 && skip >= s.rows.length) {
      skip -= s.rows.length;
      continue;
    }
    const rows = s.rows.slice(skip, skip + left);
    skip = 0;
    out.push({ group: s.group, rows });
    left -= rows.length;
  }
  return out;
}

/** The caller's translator: counts pick their plural through its `plural` (`tr.plural`). */
type Tr = Translator;

/** A group header's three parts, through the caller's translator. */
export function groupHeadParts(
  g: ServerGroup,
  tr: Tr,
): { readonly name: string; readonly count: string; readonly summary: string } {
  const name =
    g.name.kind === 'label'
      ? g.name.label
      : g.name.kind === 'address'
        ? tr('fleet.machines.group_unnamed', { address: g.name.address })
        : tr('fleet.machines.group_unknown');
  const count = tr.plural('fleet.machines.group_machines', g.count, { count: g.count });
  const s = g.summary;
  const summary =
    s.kind === 'overloaded'
      ? tr('fleet.machines.summary_overloaded', { count: s.count })
      : s.kind === 'attention'
        ? tr.plural('fleet.machines.summary_attention', s.count, { count: s.count })
        : s.kind === 'busy'
          ? tr('fleet.machines.summary_busy', { count: s.count })
          : tr('fleet.machines.summary_healthy');
  return { name, count, summary };
}

/** The header as one line, the way a person reads it. */
export function groupHeadText(g: ServerGroup, tr: Tr): string {
  const p = groupHeadParts(g, tr);
  return `${p.name} · ${p.count} · ${p.summary}`;
}

/** The Cards view's limit after one "Show more": twelve more, never past {@link CARDS_MAX}. */
export const nextCardLimit = (limit: number): number => Math.min(limit + CARDS_PAGE, CARDS_MAX);

/**
 * The Cards view's bar under the grid: "Show <n> more" while there is more to show and the
 * cap is not reached, then the quiet "Switch to List or Map" line once sixty are shown and
 * more remain. Null when every match is on screen.
 */
export function moreCards(
  total: number,
  shown: number,
): { readonly kind: 'more'; readonly count: number } | { readonly kind: 'cap' } | null {
  if (shown >= total) return null;
  if (shown >= CARDS_MAX) return { kind: 'cap' };
  return { kind: 'more', count: Math.min(CARDS_PAGE, total - shown, CARDS_MAX - shown) };
}
